import { createSelector } from '@reduxjs/toolkit';
import type { RoadSection } from '../domain/Reference';
import type { RootState } from '../../store';
import { roadSectionsQuery, selectedRoadSectionsQuery } from '../reference/queries';
import { sessionsQuery } from '../sessions/queries';
import { colorReferenceSections } from './coloring';
import { activeFilterCount, hasUsableTableFilter, matchesFilters, matchesTableFilter } from './filters';
import { formatSectionLabel } from './format';
import { neighborSections, NO_NEIGHBORS } from './neighbors';
import { buildSearchIndex } from './search';
import { attributeBreakdowns, extendHints } from './selection';
import {
    ELEMENTARY_FAMILIES,
    ElementaryFamilyKey,
    LAYER_AVAILABILITY,
    LAYER_KEYS,
    LAYER_LABELS,
    LayerKey,
    NO_INDICATOR,
    SESSIONS_DEGRADATION_INDICATOR,
} from './indicators';

const slice = (state: RootState) => state.mapV2;

export const leftPanelCollapsedQuery = createSelector(slice, (state) => state.leftPanelCollapsed);
export const fullscreenQuery = createSelector(slice, (state) => state.fullscreen);
export const basemapQuery = createSelector(slice, (state) => state.basemap);
export const rightPanelQuery = createSelector(slice, (state) => state.rightPanel);

export type Bounds = [number, number, number, number];

// Bounding box of the organization's territory, as the union of its sessions' boxes (same source as the
// current map's "zoom on organization"). Null while there is none; the referential's own extent will
// take over once its sections are loaded on this page.
export const organizationBoundsQuery = createSelector(sessionsQuery, (sessions): Bounds | null => {
    const boxes = sessions.flatMap((session) => (session.bbox ? [session.bbox] : []));
    if (boxes.length === 0) {
        return null;
    }

    return [
        Math.min(...boxes.map((box) => box[0])),
        Math.min(...boxes.map((box) => box[1])),
        Math.max(...boxes.map((box) => box[2])),
        Math.max(...boxes.map((box) => box[3])),
    ];
});

export const legendModeQuery = createSelector(slice, (state) => state.legendMode);
export const kpisQuery = createSelector(slice, (state) => state.kpis);

export type SelectionSummary =
    | { kind: 'none' }
    | { kind: 'single'; label: string; lengthMeters: number; category: string | null; gestionary: string | null }
    | { kind: 'multi'; count: number; lengthMeters: number; axes: string[] };

// What the status bar says about the selected referential segments. It reads the selection of the
// `reference` slice, shared with the /reference table: the map will drive that same selection (Phase 2).
export const selectionSummaryQuery = createSelector(
    selectedRoadSectionsQuery,
    (sections): SelectionSummary => {
        const lengthOf = (section: { cumulStart: number; cumulEnd: number }) =>
            Math.abs(section.cumulEnd - section.cumulStart);
        const [first, ...others] = sections;
        if (!first) {
            return { kind: 'none' };
        }
        if (others.length === 0) {
            return {
                kind: 'single',
                label: formatSectionLabel(first),
                lengthMeters: lengthOf(first),
                category: first.category,
                gestionary: first.gestionary,
            };
        }

        return {
            kind: 'multi',
            count: sections.length,
            lengthMeters: sections.reduce((total, section) => total + lengthOf(section), 0),
            axes: Array.from(new Set(sections.map((section) => section.roadName))),
        };
    },
);

export const activeLayerQuery = createSelector(slice, (state) => state.activeLayer);
export const sessionsDegradationTypeQuery = createSelector(slice, (state) => state.sessionsDegradationType);

export type LayerView = {
    key: LayerKey;
    label: string;
    // A layer is visible exactly when it is the active display.
    visible: boolean;
    // Expanded in the accordion.
    open: boolean;
    // Indicator shown for the layer: the active one, or the one remembered while the layer is hidden.
    indicator: string;
    available: boolean;
    applyTableFilters: boolean;
    // Sessions only: a second selector is needed to pick the degradation type.
    needsSecondarySelector: boolean;
};

export const layersQuery = createSelector(slice, (state): LayerView[] =>
    LAYER_KEYS.map((key) => ({
        key,
        label: LAYER_LABELS[key],
        visible: state.activeLayer === key,
        open: state.openLayer === key,
        indicator: state.indicators[key],
        available: LAYER_AVAILABILITY[key],
        applyTableFilters: state.applyTableFilters[key],
        needsSecondarySelector: key === 'sessions' && state.indicators.sessions === SESSIONS_DEGRADATION_INDICATOR,
    })),
);

// "RÉFÉRENTIEL > CATÉGORIE": shared by the active-display chip and the legend title.
export const activeDisplayTitleQuery = createSelector(slice, (state): string | null => {
    if (!state.activeLayer) {
        return null;
    }

    return `${LAYER_LABELS[state.activeLayer]} > ${state.indicators[state.activeLayer]}`.toUpperCase();
});

export const elementaryEnabledQuery = createSelector(slice, (state) => state.elementaryEnabled);
export const elementaryOpenQuery = createSelector(slice, (state) => state.elementaryOpen);

export type ElementaryCheckState = 'checked' | 'unchecked' | 'indeterminate';

export type ElementaryFamilyView = {
    key: ElementaryFamilyKey;
    label: string;
    checkState: ElementaryCheckState;
    // Families without children (e.g. "Pas élémentaires") cannot be expanded.
    expandable: boolean;
    expanded: boolean;
    children: Array<{ label: string; checked: boolean }>;
};

export const elementaryFamiliesQuery = createSelector(slice, (state): ElementaryFamilyView[] =>
    ELEMENTARY_FAMILIES.map(({ key, label, children }) => {
        const family = state.elementary[key];
        const expanded = children.length > 0 && state.elementaryExpanded[key];
        if (children.length === 0) {
            return {
                key,
                label,
                checkState: family.on ? 'checked' : 'unchecked',
                expandable: false,
                expanded: false,
                children: [],
            };
        }
        const checkedCount = children.filter((child) => family.kids[child]).length;
        let checkState: ElementaryCheckState = 'indeterminate';
        if (checkedCount === 0) {
            checkState = 'unchecked';
        } else if (checkedCount === children.length) {
            checkState = 'checked';
        }

        return {
            key,
            label,
            checkState,
            expandable: true,
            expanded,
            children: children.map((child) => ({ label: child, checked: !!family.kids[child] })),
        };
    }),
);

// Selection, as the object sheet and the map need it. The ids come straight from the `reference` slice
// (a stable reference), so the map only re-applies its highlight when the selection really changes.
export const selectedIdsQuery = (state: RootState) => state.reference.selectedRoadSectionIds;

export const singleSelectedSectionQuery = createSelector(selectedRoadSectionsQuery, (sections) =>
    sections.length === 1 ? sections[0] : null,
);

export const neighborSectionsQuery = createSelector(
    singleSelectedSectionQuery,
    roadSectionsQuery,
    (current, sections) => (current ? neighborSections(sections, current) : NO_NEIGHBORS),
);

export const focusRequestQuery = (state: RootState) => state.mapV2.focusRequest;
export const listHoveredIdQuery = (state: RootState) => state.mapV2.listHoveredId;

// Same/mixed summary of the referential attributes of the selected sections (multi-selection panel).
export const attributeBreakdownsQuery = createSelector(selectedRoadSectionsQuery, (sections) =>
    attributeBreakdowns(sections),
);

// The three "extend the selection" actions, keyed on the first selected section.
export const extendActionsQuery = createSelector(
    selectedIdsQuery,
    selectedRoadSectionsQuery,
    roadSectionsQuery,
    (ids, selected, all) => {
        const base = selected.find((section) => section.id === ids[0]) ?? selected[0];

        return base ? { baseId: base.id, hints: extendHints(all, base) } : null;
    },
);

export const editDialogQuery = (state: RootState) => state.mapV2.editDialog;
export const noticeQuery = (state: RootState) => state.mapV2.notice;

export const filtersQuery = (state: RootState) => state.mapV2.filters;
export const activeFilterCountQuery = createSelector(filtersQuery, activeFilterCount);

export interface Perimeter {
    total: number;
    totalLengthMeters: number;
    // The sections kept by the filters (all of them when nothing filters).
    matching: RoadSection[];
    matchingLengthMeters: number;
    // The sections the filters leave out, to dim on the map.
    excludedIds: string[];
    // True when the panel's criteria or the table's filters (if applied to the map) narrow the référentiel.
    restricted: boolean;
    includes: (id: string) => boolean;
}

const lengthOf = (section: RoadSection) => Math.abs(section.cumulEnd - section.cumulStart);

// The part of the référentiel the map shows: filter panel criteria, and the table's filters when the layer's
// "Appliquer les filtres de la vue tabulaire" switch is on.
export const perimeterQuery = createSelector(
    roadSectionsQuery,
    filtersQuery,
    (state: RootState) => state.mapV2.applyTableFilters.reference,
    (state: RootState) => state.reference.filter,
    (sections, filters, applyTable, tableFilter): Perimeter => {
        const useTable = applyTable && hasUsableTableFilter(tableFilter);
        const restricted = activeFilterCount(filters) > 0 || useTable;
        const totalLengthMeters = sections.reduce((total, section) => total + lengthOf(section), 0);
        if (!restricted) {
            return {
                total: sections.length,
                totalLengthMeters,
                matching: sections,
                matchingLengthMeters: totalLengthMeters,
                excludedIds: [],
                restricted,
                includes: () => true,
            };
        }
        const matching: RoadSection[] = [];
        const excludedIds: string[] = [];
        sections.forEach((section) => {
            if (matchesFilters(section, filters) && (!useTable || matchesTableFilter(section, tableFilter))) {
                matching.push(section);
            } else {
                excludedIds.push(section.id);
            }
        });
        const matchingIds = new Set(matching.map((section) => section.id));

        return {
            total: sections.length,
            totalLengthMeters,
            matching,
            matchingLengthMeters: matching.reduce((total, section) => total + lengthOf(section), 0),
            excludedIds,
            restricted,
            includes: (id) => matchingIds.has(id),
        };
    },
);

// Colors and legend of the référentiel layer, or null when it is not displayed or has no indicator ("–").
// Computed on the filtered sections, so the legend describes what the map shows.
export const referenceColoringQuery = createSelector(
    activeLayerQuery,
    (state: RootState) => state.mapV2.indicators.reference,
    perimeterQuery,
    (activeLayer, indicator, perimeter) =>
        activeLayer === 'reference' && indicator !== NO_INDICATOR
            ? colorReferenceSections(perimeter.matching, indicator)
            : null,
);

export interface SectionPaint {
    color: string | null;
    dim: boolean;
}

// What to paint on each section of the map: its indicator color, and whether the filters dim it.
// Null when nothing needs painting. Sections with nothing to paint are left out.
export const referencePaintQuery = createSelector(
    referenceColoringQuery,
    perimeterQuery,
    activeLayerQuery,
    (coloring, perimeter, activeLayer): Map<string, SectionPaint> | null => {
        const dimmed = activeLayer === 'reference' && perimeter.restricted;
        if (!coloring && !dimmed) {
            return null;
        }
        const paint = new Map<string, SectionPaint>();
        coloring?.colorById.forEach((color, id) => paint.set(id, { color, dim: false }));
        if (dimmed) {
            perimeter.excludedIds.forEach((id) => paint.set(id, { color: null, dim: true }));
        }

        return paint;
    },
);

// Searchable text of every section, built once per loaded référentiel.
export const searchIndexQuery = createSelector(roadSectionsQuery, buildSearchIndex);
